import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { RiCheckLine, RiCloseLine } from '@remixicon/react';
import { setBackground } from '../redux/slices/canvasSlice';
import { fetchBackgroundTemplates } from '../redux/slices/assetsSlice';

const BackgroundSelector = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [colorValue, setColorValue] = useState('#FFFFFF');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  
  const { backgroundTemplates, backgroundTemplatesLoading } = useSelector(
    (state) => state.assets
  );
  
  const currentBackground = useSelector((state) => state.canvas.background);
  
  useEffect(() => {
    if (open && backgroundTemplates.length === 0) {
      dispatch(fetchBackgroundTemplates());
    }
  }, [open, dispatch, backgroundTemplates.length]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleColorSelect = () => {
    dispatch(setBackground({ type: 'color', value: colorValue }));
    onClose();
  };
  
  const handleImageSelect = (url) => {
    dispatch(setBackground({ type: 'image', value: url || imageUrl }));
    onClose();
  };
  
  const handleVideoSelect = (url) => {
    dispatch(setBackground({ type: 'video', value: url || videoUrl }));
    onClose();
  };
  
  // Mock background templates (replace with actual data from API)
  const mockBackgrounds = backgroundTemplates.length > 0 ? backgroundTemplates : [
    {
      id: 1,
      type: 'image',
      url: 'https://images.unsplash.com/photo-1557683316-973673baf926',
      thumbnail: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=300',
      name: 'Gradient Blue',
    },
    {
      id: 2,
      type: 'image',
      url: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809',
      thumbnail: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=300',
      name: 'Gradient Purple',
    },
    {
      id: 3,
      type: 'image',
      url: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d',
      thumbnail: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=300',
      name: 'Abstract Red',
    },
  ];
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Select Background</Typography>
          <IconButton onClick={onClose} size="small">
            <RiCloseLine size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ marginBottom: 3 }}>
          <Tab label="Solid Color" />
          <Tab label="Image" />
          <Tab label="Video" />
        </Tabs>
        
        {/* Solid Color Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 3 }}>
              <TextField
                type="color"
                value={colorValue}
                onChange={(e) => setColorValue(e.target.value)}
                sx={{ width: 100 }}
              />
              <TextField
                label="Color Value"
                value={colorValue}
                onChange={(e) => setColorValue(e.target.value)}
                fullWidth
              />
            </Box>
            
            {/* Preset Colors */}
            <Typography variant="subtitle2" gutterBottom>
              Preset Colors
            </Typography>
            <Grid container spacing={1}>
              {[
                '#FFFFFF', '#000000', '#C61F2F', '#5B1DB7', '#10B981',
                '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899',
              ].map((color) => (
                <Grid item key={color}>
                  <Box
                    onClick={() => setColorValue(color)}
                    sx={{
                      width: 50,
                      height: 50,
                      backgroundColor: color,
                      border: colorValue === color ? '3px solid #C61F2F' : '1px solid #E0E0E0',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ marginTop: 3, textAlign: 'right' }}>
              <IconButton
                onClick={handleColorSelect}
                color="primary"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                <RiCheckLine size={24} />
              </IconButton>
            </Box>
          </Box>
        )}
        
        {/* Image Tab */}
        {activeTab === 1 && (
          <Box>
            <TextField
              label="Image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              fullWidth
              sx={{ marginBottom: 3 }}
              helperText="Enter a URL or select from templates below"
            />
            
            {backgroundTemplatesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {mockBackgrounds
                  .filter((bg) => bg.type === 'image')
                  .map((bg) => (
                    <Grid item xs={4} key={bg.id}>
                      <Card
                        onClick={() => handleImageSelect(bg.url)}
                        sx={{
                          cursor: 'pointer',
                          border:
                            currentBackground.value === bg.url
                              ? '3px solid #C61F2F'
                              : '1px solid transparent',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="140"
                          image={bg.thumbnail}
                          alt={bg.name}
                        />
                        <CardContent>
                          <Typography variant="caption">{bg.name}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        )}
        
        {/* Video Tab */}
        {activeTab === 2 && (
          <Box>
            <TextField
              label="Video URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              fullWidth
              sx={{ marginBottom: 3 }}
              helperText="Enter a video URL (MP4, WebM)"
            />
            
            <Box sx={{ textAlign: 'right' }}>
              <IconButton
                onClick={() => handleVideoSelect()}
                color="primary"
                disabled={!videoUrl}
                sx={{
                  backgroundColor: videoUrl ? 'primary.main' : 'grey.300',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: videoUrl ? 'primary.dark' : 'grey.400',
                  },
                }}
              >
                <RiCheckLine size={24} />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BackgroundSelector;



